import type { Messages } from "@/i18n/locales/en";
import { legalMessagesSk } from "@/i18n/locales/legalSkData";
import { pagesMessagesSk } from "@/i18n/locales/pagesSkData";

export const skMessages = {
  meta: {
    description:
      "WasOK — skenujte produkty, ukladajte hodnotenia a poznámky, zapamätajte si, čo stálo za nákup.",
    title: "WasOK — Zapamätajte si, čo stálo za nákup",
  },
  header: {
    navFeatures: "Funkcie",
    navHowItWorks: "Ako to funguje",
    navPrivacy: "Súkromie",
    navUseCases: "Príklady použitia",
    download: "Stiahnuť",
    openMenu: "Otvoriť menu",
    closeMenu: "Zavrieť menu",
    languageAria: "Jazyk",
  },
  hero: {
    badge: "📱 Teraz dostupné na Androide",
    title: "Zapamätajte si, čo stálo za nákup.",
    subtitle:
      "Skenujte produkty, ukladajte hodnotenia a poznámky a okamžite si spomeňte, či bol produkt naozaj dobrý.",
    googlePlay: "Stiahnuť z Google Play",
    watchDemo: "Pozrieť demo",
    privacyLine: "Všetky údaje zostávajú na vašom zariadení.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Skenujte, aby ste si zapamätali",
    snackCard: "Výborné!",
    coffeeCard: "Príliš horká",
    scrollHint: "Posuňte sa a objavujte",
  },
  problems: {
    heading: "Znie to povedome?",
    subheading:
      "Všetci to poznáme — stáť pred produktmi a snažiť sa spomenúť, či sa nám minule páčili.",
    notedAgo: "✓ Poznačili ste si to pred 3 mesiacmi",
    items: [
      {
        question: "Ktorú čokoládu som si naozaj obľúbil?",
        note: "Perfektná sladkosť",
      },
      {
        question: "Prečo som prestal kupovať tento šampón?",
        note: "Vysušil mi vlasy",
      },
      {
        question: "Bola táto káva dobrá alebo sklamanie?",
        note: "Príliš horká",
      },
    ],
  },
  howItWorks: {
    heading: "Ako to funguje",
    subheading:
      "Štyri jednoduché kroky, aby ste už nikdy nezabudli svoje názory na produkty.",
    stepPrefix: "Krok",
    steps: [
      {
        title: "Naskenujte akýkoľvek čiarový kód produktu",
        description:
          "Rýchle a jednoduché skenovanie fotoaparátom. Funguje s každým čiarovým kódom.",
      },
      {
        title: "Uložte hodnotenia, komentáre a fotografie",
        description:
          "Hodnoťte produkty, pridávajte poznámky, pripájajte fotografie a zaznamenávajte ceny a miesta.",
      },
      {
        title: "Okamžite si spomeňte na svoju skúsenosť",
        description:
          "Neskôr naskenujte ten istý produkt a uvidíte predchádzajúce hodnotenie a poznámky.",
      },
      {
        title: "Vytvárajte inteligentné nákupné zoznamy",
        description:
          "Vytvárajte nákupné zoznamy z produktov, ktorým už dôverujete a máte ich radi.",
      },
    ],
  },
  privacy: {
    heading: "Vaša nákupná história zostáva vaša.",
    body: "WasOK vám pomáha zapamätať si produkty, ktoré ste vyskúšali, pričom hodnotenia, poznámky, fotografie a nákupné zoznamy ukladá lokálne na vašom zariadení.",
    trustBanner:
      "💚 Postavené okolo vašej nákupnej histórie — nie okolo zbierania vašich údajov.",
    bullets: [
      "Nie je potrebný účet",
      "Nie je potrebná synchronizácia s cloudom",
      "Lokálne úložisko",
      "Zálohy pod kontrolou používateľa",
      "Voliteľné vyhľadávanie podľa čiarového kódu pre informácie o produkte.",
      "Podporované reklamou cez Google AdMob",
    ],
  },
  features: {
    heading: "Všetko, čo potrebujete",
    subheading:
      "Výkonné funkcie navrhnuté tak, aby sledovanie produktov bolo jednoduché a súkromné.",
    items: [
      {
        title: "Skener čiarových kódov",
        description: "Rýchle a presné skenovanie čiarových kódov pre akýkoľvek produkt.",
      },
      {
        title: "Hodnotenia produktov",
        description: "Hodnoťte produkty od 1 do 5 hviezdičiek, aby ste si zapamätali kvalitu.",
      },
      {
        title: "Fotografie produktov",
        description: "Pripájajte fotografie na zapamätanie obalu alebo detailov.",
      },
      {
        title: "Kategórie",
        description: "Organizujte produkty do vlastných kategórií.",
      },
      {
        title: "Sledovanie obchodov",
        description: "Zapamätajte si, kde ste každý produkt kúpili.",
      },
      {
        title: "Zaznamenávanie cien",
        description: "Sledujte ceny a nájdite najlepšie ponuky.",
      },
      {
        title: "Nákupné zoznamy",
        description: "Vytvárajte zoznamy z obľúbených produktov.",
      },
      {
        title: "Offline prístup",
        description: "Funguje dokonale aj bez internetového pripojenia.",
      },
      {
        title: "Lokálne úložisko",
        description: "Všetky vaše údaje sú bezpečne uložené na vašom zariadení.",
      },
    ],
  },
  useCases: {
    heading: "Ideálne pre každý regál",
    subheading:
      "Či už nakupujete potraviny, kozmetiku alebo domácnosť — WasOK vám pomôže zapamätať si.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacky a pochúťky",
        description: "Zapamätajte si, ktoré sladkosti boli naozaj dobré.",
      },
      {
        emoji: "☕",
        title: "Káva a nápoje",
        description: "Sledujte kávy, ktoré stoja za ďalší nákup.",
      },
      {
        emoji: "💄",
        title: "Kozmetika a starostlivosť o pleť",
        description: "Píšte poznámky o reakciách pokožky.",
      },
      {
        emoji: "🧹",
        title: "Domáce potreby",
        description: "Vyhnite sa opätovnému nákupu produktov, ktoré sklamali.",
      },
    ],
  },
  screenshots: {
    heading: "Krásne a intuitívne",
    subheading:
      "Premyslene navrhnuté rozhranie, vďaka ktorému je sledovanie produktov radosťou.",
    items: [
      { title: "Skener" },
      { title: "Detail produktu" },
      { title: "Zadanie hodnotenia" },
      { title: "Hľadanie a história" },
      { title: "Nákupný zoznam" },
    ],
  },
  cta: {
    heading: "Pamäť na nákupy do skutočného života.",
    body: "Prestaňte zabúdať, ktoré produkty stáli za nákup. Začnite nakupovať rozumnejšie ešte dnes.",
    googlePlay: "Stiahnuť z Google Play",
    scanQr: "Naskenujte a stiahnite",
    tagline: "Súkromné · Offline · Jednoduché",
  },
  footer: {
    tagline: "Zapamätajte si, čo stálo za nákup.",
    linksHeading: "Odkazy",
    privacyPolicy: "Zásady ochrany súkromia",
    terms: "Podmienky používania",
    contact: "Kontakt",
    linkFaq: "FAQ",
    linkLicenses: "Licencie open source",
    linkAdvertising: "Reklama a monetizácia",
    downloadHeading: "Stiahnuť",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Všetky práva vyhradené. Vytvorené s 💚 pre rozumnejšie nakupovanie.",
  },
  legal: legalMessagesSk,
  faq: pagesMessagesSk.faq,
  support: pagesMessagesSk.support,
  advertising: pagesMessagesSk.advertising,
} satisfies Messages;
